// A material sold in packs (Spencer, 2026-10-17): the price is the vendor's for the pack, the pack size how many
// pieces it holds, and the tick says whether a job pays for whole packs (a pull) or only the pieces it uses (a hinge
// kept in stock). One set of words and one control for it, wherever a material's price is shown or signed.

import { materialPack, type BomPack } from '@zerogap/engine';
import type { Row } from '../lib/api';
import { useRows } from '../lib/api';
import { Edit } from './kit';

const asMaterial = (m: Row) => ({ price: m.price ?? null, pack_size: m.pack_size ?? null, whole_packs: !!m.whole_packs });
const dollars = (text: string) => `$${Number(text).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`;

/** "$39.59 per pack of 10 · $3.959 each · a job pays for whole packs", or "" for a material priced per piece. */
export function packWords(m: Row): string {
  const p = materialPack(asMaterial(m));
  if (!p) return '';
  const size = p.size.toDecimal(4);
  return `${dollars(p.price.toDecimal(4))} per pack of ${size} · ${dollars(p.each.toDecimal(4))} each · ${p.whole ? 'a job pays for whole packs' : 'a job pays for the pieces it uses'}`;
}

/** A material's price as one line: by the pack where it has a pack size, else per its unit. */
export function priceWords(m: Row): string {
  if (m.price === null || m.price === undefined || m.price === '') return 'no price';
  return packWords(m) || `${dollars(String(m.price))} per ${m.unit || 'each'}`;
}

/**
 * A material that reads as priced per pack (its unit says "per 10" or "/4", or its name "(100)" or "pack of 25") and has
 * no pack size yet. A pack size of 1 says the price is already for what its lines count (a reel, when the lines count reels).
 */
export function looksPerPack(m: Row): boolean {
  if (m.pack_size !== null && m.pack_size !== undefined && String(m.pack_size).trim() !== '') return false;
  const unit = String(m.unit ?? '').trim();
  const name = String(m.description ?? '');
  return /^(per\s*\d|\/\s*\d)/i.test(unit) || /^(pack|box|bag|case)\b/i.test(unit) || /\(\s*\d+\s*\)|\bpack of \d+|\b\d+\s*-?\s*(pk|pack|pcs|ct|count)\b/i.test(name);
}

const sizeParse = (x: string) => (x.trim() === '' ? '' : /^\d*\.?\d+$/.test(x.trim()) && Number(x) > 0 ? x.trim() : undefined);

/** The pack size and the whole-packs tick, editable, with what one piece costs. Saving either returns the material to unverified. */
export function PackFields({ m, disabled, onSave }: { m: Row; disabled?: boolean; onSave: (patch: Row) => void | Promise<unknown> }) {
  const words = packWords(m);
  return (
    <span className="row mid" style={{ gap: 6 }}>
      <small>pack of</small>
      <Edit className="cell short mono right" disabled={disabled} value={m.pack_size ?? ''} placeholder="1" parse={sizeParse} onSave={(v) => onSave({ pack_size: v === '' ? null : v })} />
      <label className="row mid nowrap" style={{ gap: 4 }}>
        <input type="checkbox" disabled={disabled} checked={!!m.whole_packs} onChange={(e) => void onSave({ whole_packs: e.target.checked })} />
        <small>a job pays for whole packs</small>
      </label>
      {words && <small className="muted">{words.split(' · ')[1]}</small>}
    </span>
  );
}

/** Where a material is used: the recipe lines that name it, and the lines that read it through a standard that picks it. */
export function MaterialUses({ m }: { m: Row }) {
  const lines = useRows('recipe_bom_line');
  const recipes = useRows('recipe');
  const items = useRows('catalog_item');
  const defs = useRows('standard_def');
  const picked = useRows('job_standard', { eq: { value: m.id } });
  const typeLines = useRows('job_type_bom_line', { eq: { material_id: m.id } });
  const recipe = (id: string) => recipes.rows.find((r) => r.id === id);
  const item = (id: string) => items.rows.find((c) => c.id === id);
  // a standard that picks this material, as the company default or on a job, fills every line that reads its role
  const roles = defs.rows.filter((d) => d.data_type === 'material' && (d.default_value === m.id || picked.rows.some((p) => p.standard_def_id === d.id)));
  const named = (b: Row) => {
    const r = recipe(b.recipe_id);
    const c = r ? item(r.catalog_item_id) : undefined;
    return `${c?.name ?? '?'} · ${r?.name ?? '?'}: ${b.qty_expr}`;
  };
  const direct = lines.rows.filter((b) => b.material_id === m.id).map(named);
  const viaRole = roles.flatMap((d) => lines.rows.filter((b) => b.material_role === d.key).map((b) => `${named(b)} (through ${d.label})`));
  const all = [...direct, ...viaRole, ...(typeLines.rows.length ? [`${typeLines.rows.length} job type line${typeLines.rows.length === 1 ? '' : 's'}`] : [])];
  if (lines.loading || recipes.loading) return <small className="muted">…</small>;
  if (all.length === 0) return <small className="muted">No recipe line uses it.</small>;
  return (
    <div>
      {all.slice(0, 12).map((t, i) => (
        <div key={i}>
          <small className="mono">{t}</small>
        </div>
      ))}
      {all.length > 12 && <small className="muted">and {all.length - 12} more</small>}
    </div>
  );
}

/** A job's line of a material sold in packs, in words: "2 packs of 10 at $39.59 for the job, 9 left over", or "pack of 10 at $39.59, one piece $3.959". */
export function bomPackWords(pack: BomPack | undefined): string {
  if (!pack) return '';
  const size = Number(pack.size).toString();
  if (pack.whole) {
    const n = Number(pack.packs ?? 0);
    return `${n} pack${n === 1 ? '' : 's'} of ${size} at ${dollars(pack.price)} for the job, ${Number(pack.left_over ?? 0)} left over`;
  }
  return `pack of ${size} at ${dollars(pack.price)}, one piece ${dollars(pack.each)}`;
}
